import { ChangeEvent, memo, useEffect, useRef, useState } from "react";
import CustomReactSelect from "@app/components/custom-react-select";
import { Images } from "@assets/images";
import DashboardService from "@app/services/http/dashboard.service";
import { VoucherActiveInteraction } from "@app/types/dashboard.type";
import { DEFAULT_DATE_FORMAT, DEFAULT_PAGE, OrderType } from "@app/constants";
import { formatDate, numberWithCommas } from "@app/utils/util";
import { ColumnConfig } from "@app/components/table/table.component";
import { ChartConfig, LabelValue, StatisticsState } from "@app/types";
import BookService from "@app/services/http/book.service";
import useCancellationRequest from "@core/hooks/use-cancellation-request.hook";
import Popover from "@app/components/popover";
import CalendarIcon from "@app/icons/calendar";
import Checkbox from "@app/components/checkbox";
import Button from "@app/components/button";
import UserSquareIcon from "@app/icons/user-square";
import CustomAsyncMultiSelect from "@app/components/custom-async-multi-select";
import Radio from "@app/components/radio";
import { PopoverRef } from "@app/components/popover/popover.component";
import Input from "@app/components/input";
import BarChart from "@app/components/bar-chart/bar-chart.component";
import CustomTable from "./custom-table.component";
import {
  calculateDateTemp,
  generateVoucherChartConfig,
  initialVoucherChartConfig,
  initialStateStatistics,
} from "../helper";
import { cloneDeep } from "lodash";
import { StatisticsType } from "@app/constants/statistics.const";

function BookNameColumnTable({ data }: { data: VoucherActiveInteraction }) {
  return (
    <div className="flex items-center gap-2">
      <img src={data.bookThumbnail} className="h-9 w-9 rounded-md border" />
      {data.bookName}
    </div>
  );
}

function ViewCountColumnTable({ data }: { data: VoucherActiveInteraction }) {
  return <div className="align-middle">{data.viewCount}</div>;
}

function ListenCountColumnTable({ data }: { data: VoucherActiveInteraction }) {
  return <div className="align-middle">{data.listenCount}</div>;
}

function TotalTimeColumnTable({ data }: { data: VoucherActiveInteraction }) {
  return <div className="align-middle">{data.totalTime} phút</div>;
}

const columnConfig: ColumnConfig[] = [
  {
    label: "Tên sách",
    dataKey: "bookName",
    width: "40%",
    tdClassName: "truncate max-w-[40%]",
    hasOrder: true,
    component: BookNameColumnTable,
  },
  {
    label: "Số lượt xem",
    dataKey: "viewCount",
    width: "20%",
    tdClassName: "truncate max-w-[20%]",
    hasOrder: true,
    component: ViewCountColumnTable,
  },
  {
    label: "Số lượt nghe",
    dataKey: "listenCount",
    width: "20%",
    tdClassName: "truncate max-w-[20%]",
    hasOrder: true,
    component: ListenCountColumnTable,
  },
  {
    label: "Tổng thời gian nghe",
    dataKey: "totalTime",
    width: "20%",
    tdClassName: "truncate max-w-[20%]",
    hasOrder: true,
    component: TotalTimeColumnTable,
  },
];

const StatisticsVoucher = () => {
  const { abortController } = useCancellationRequest();
  const inputVoucherCodeRef = useRef<HTMLInputElement>(null);
  const popoverRef1 = useRef<PopoverRef>(null);
  const popoverRef2 = useRef<PopoverRef>(null);

  const [state, _setState] = useState<StatisticsState>(initialStateStatistics);
  const setState = (data: Partial<StatisticsState> = {}) =>
    _setState((pre) => ({ ...pre, ...data }));

  const [chartConfigUser, setChartConfigUser] = useState<ChartConfig>(
    initialVoucherChartConfig
  );
  const [chartConfigInteraction, setChartConfigInteraction] =
    useState<ChartConfig>(initialVoucherChartConfig);

  const handleFilterUserType = () => {
    setState({ userType: [...state.userTypeTemp] });
    popoverRef2.current?.close();
  };

  const handleFilterDate = () => {
    setState({
      startDate: state.startDateTemp,
      endDate: state.endDateTemp,
    });
    popoverRef1.current?.close();
  };

  const handleChangeCheckboxUserType = (
    event: ChangeEvent<HTMLInputElement>
  ) => {
    const userTypeTemp = [...state.userTypeTemp];

    if (event.target.checked) {
      userTypeTemp.push(event.target.value);
    } else {
      userTypeTemp.splice(userTypeTemp.indexOf(event.target.value), 1);
    }

    setState({
      userTypeTemp,
    });
  };

  const handleChangeRadioDate = (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.value === "custom") {
      setState({
        startDateTemp: undefined,
        endDateTemp: undefined,
        filterDate: event.target.value,
      });
    } else {
      const { startDateTemp, endDateTemp } = calculateDateTemp(
        event.target.value,
        state.startDateTemp,
        state.endDateTemp
      );

      setState({
        startDateTemp,
        endDateTemp,
        filterDate: event.target.value,
      });
    }
  };

  const getBooksOptions = (inputValue: string) => {
    return new Promise<LabelValue[]>((resolve) => {
      setTimeout(() => {
        BookService.getBookDropdown(
          {
            keyword: inputValue.trim(),
            bookOption: 2,
            pageSize: 20,
          },
          abortController.signal,
          false
        ).then((res) => {
          const data: LabelValue[] = res.data.items
            .map((item) => ({ label: item.name, value: item.id }))
            .filter((item) => {
              if (inputValue.trim()) {
                return (
                  item.label
                    .toLowerCase()
                    .normalize("NFD")
                    .replace(/[\u0300-\u036f]/g, "")
                    .indexOf(
                      inputValue
                        .trim()
                        .toLowerCase()
                        .normalize("NFD")
                        .replace(/[\u0300-\u036f]/g, "")
                    ) >= 0
                );
              }

              return true;
            });

          resolve(data);
        });
      }, 300);
    });
  };

  useEffect(() => {
    if (state.voucherCode) {
      DashboardService.getVoucherActiveUses({
        voucherCode: state.voucherCode,
        startDate: state.startDate,
        endDate: state.endDate,
        userType: state.userType,
      }).then((res) => {
        const response = res.data.items;

        const text = res.data.totalUser
          ? `Tổng số lượng user: ${numberWithCommas(res.data.totalUser)}`
          : "";
        const labels = response.map((item) =>
          formatDate(item.date, DEFAULT_DATE_FORMAT)
        );
        const data = response.map((item) => item.userCount);

        setChartConfigUser(
          generateVoucherChartConfig({
            text,
            labels,
            data,
          })
        );
      });

      DashboardService.getVoucherActiveInteraction({
        voucherCode: state.voucherCode,
        pageNum: DEFAULT_PAGE,
        pageSize: 100,
        sortBy: "bookName",
        sortType: OrderType.ASC,
        startDate: state.startDate,
        endDate: state.endDate,
        userType: state.userType,
        bookIds: state.bookIds?.map((item) => item.value),
      }).then((res) => {
        const response = res.data.items;

        setState({
          dataVoucherActiveInteraction: response,
          metaVoucherActiveInteraction: res.data.meta,
          totalListener: res.data.totalListener,
        });

        const text = res.data.totalListener
          ? `Tổng số lượt nghe: ${numberWithCommas(res.data.totalListener)}`
          : "";
        const labels = response.map((item) => item.bookName);
        const data = response.map(
          (item) => item[state.typeChart.value as keyof typeof item]
        ) as number[];

        setChartConfigInteraction(
          generateVoucherChartConfig({
            text,
            labels,
            data,
          })
        );
      });
    }
  }, [
    state.voucherCode,
    state.bookIds,
    state.userType.length,
    state.startDate,
    state.endDate,
  ]);

  useEffect(() => {
    if (state.typeChart) {
      const dataVoucherActiveInteraction = cloneDeep(
        state.dataVoucherActiveInteraction
      );

      const text = `Tổng số lượt nghe: ${numberWithCommas(state.totalListener)}`;
      const labels = dataVoucherActiveInteraction.map((item) => item.bookName);
      const data = dataVoucherActiveInteraction.map(
        (item) => item[state.typeChart.value as keyof typeof item]
      ) as number[];

      setChartConfigInteraction(
        generateVoucherChartConfig({
          text,
          labels,
          data,
        })
      );
    }
  }, [state.typeChart]);

  return (
    <div className="mt-6 rounded-xl bg-white py-6 px-5">
      <div className="flex flex-col 2xl:flex-row justify-between align-middle gap-4 2xl:gap-0">
        <div className="flex items-center gap-3">
          <div className="text-lg">Thống kê voucher</div>
          <div>
            <CustomReactSelect
              options={StatisticsType}
              value={state.typeChart}
              // styles={{
              //   stylesControl: {
              //     background: "#5570F114",
              //     border: "none",
              //     borderRadius: "0.5rem",
              //     color: "#5570F1",
              //     width: "180px",
              //   },
              //   styleSingle: {
              //     color: "#5570F1",
              //   },
              //   dropdownIndicatorStyles: {
              //     color: "#5570F1",
              //   },
              // }}
              onChange={(val) => setState({ typeChart: val })}
            />
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Input
            ref={inputVoucherCodeRef}
            placeholder="Nhập mã voucher"
            leftIcon={Images.SearchIcon.default}
            value={state.voucherCode}
            onChange={(val) => setState({ voucherCode: val.target.value })}
            timeDelay={500}
            height={"[36px]"}
          />
          <div>
            <Popover
              ref={popoverRef1}
              content={
                <div className="w-[320px]">
                  <div>By date</div>
                  <div>
                    <div className="mt-4 flex gap-4">
                      <Radio
                        name="date"
                        label="This week"
                        value={"thisWeek"}
                        onChange={handleChangeRadioDate}
                        className="flex-1"
                        checked={state.filterDate === "thisWeek"}
                      />
                      <Radio
                        name="date"
                        label="Last week"
                        value={"lastWeek"}
                        onChange={handleChangeRadioDate}
                        className="flex-1"
                        checked={state.filterDate === "lastWeek"}
                      />
                    </div>
                    <div className="mt-4 flex gap-4">
                      <Radio
                        name="date"
                        label="This month"
                        value={"thisMonth"}
                        onChange={handleChangeRadioDate}
                        className="flex-1"
                        checked={state.filterDate === "thisMonth"}
                      />
                      <Radio
                        name="date"
                        label="Last month"
                        value={"lastMonth"}
                        onChange={handleChangeRadioDate}
                        className="flex-1"
                        checked={state.filterDate === "lastMonth"}
                      />
                    </div>
                    <div className="mt-4 flex gap-4">
                      <Radio
                        name="date"
                        label="This year"
                        value={"thisYear"}
                        onChange={handleChangeRadioDate}
                        className="flex-1"
                        checked={state.filterDate === "thisYear"}
                      />
                      <Radio
                        name="date"
                        label="Last year"
                        value={"lastYear"}
                        onChange={handleChangeRadioDate}
                        className="flex-1"
                        checked={state.filterDate === "lastYear"}
                      />
                    </div>
                  </div>
                  <div className="mt-4 flex gap-4">
                    <Radio
                      name="date"
                      label="Custom"
                      value={"custom"}
                      onChange={handleChangeRadioDate}
                      className="flex-1"
                      checked={state.filterDate === "custom"}
                    />
                  </div>
                  {state.filterDate === "custom" && (
                    <div className="mt-4 flex gap-4 justify-between">
                      <div className="w-[48%]">
                        <Input
                          type="date"
                          placeholder="Start date"
                          height="[30px]"
                          onChange={(e) =>
                            _setState((state) => ({
                              ...state,
                              startDateTemp: e.target.value,
                            }))
                          }
                          value={state.startDateTemp}
                        />
                      </div>
                      <div className="w-[48%]">
                        <Input
                          type="date"
                          placeholder="End date"
                          height="[30px]"
                          onChange={(e) =>
                            _setState((state) => ({
                              ...state,
                              endDateTemp: e.target.value,
                            }))
                          }
                          value={state.endDateTemp}
                        />
                      </div>
                    </div>
                  )}
                  <div className="mt-4 w-full">
                    <Button
                      label="Filter"
                      theme="primary"
                      containerClassName="w-full"
                      onClick={handleFilterDate}
                    />
                  </div>
                </div>
              }
              placement="bottomRight"
            >
              <div className="flex cursor-pointer items-center gap-2 rounded-lg border border-solid border-[#5570F1] bg-white px-2 py-1 text-[#5570F1]">
                <CalendarIcon />
                <div>Filter</div>
              </div>
            </Popover>
          </div>
          <div>
            <Popover
              ref={popoverRef2}
              content={
                <div>
                  <div>By user type</div>

                  <div className="my-4 flex gap-4">
                    <Checkbox
                      label="ALL"
                      value="ALL"
                      onChange={handleChangeCheckboxUserType}
                      checked={state.userTypeTemp.includes("ALL")}
                    />
                    <Checkbox
                      label="VIP"
                      value="VIP"
                      onChange={handleChangeCheckboxUserType}
                      checked={state.userTypeTemp.includes("VIP")}
                    />
                    <Checkbox
                      label="FREE"
                      value="FREE"
                      onChange={handleChangeCheckboxUserType}
                      checked={state.userTypeTemp.includes("FREE")}
                    />
                  </div>
                  <div>
                    <Button
                      label="Filter"
                      theme="primary"
                      containerClassName="w-full"
                      onClick={handleFilterUserType}
                    />
                  </div>
                </div>
              }
              placement="bottomRight"
            >
              <div className="flex cursor-pointer items-center gap-2 rounded-lg border border-solid border-[#5570F1] bg-white px-2 py-1 text-[#5570F1]">
                <UserSquareIcon />
                <div>Filter</div>
              </div>
            </Popover>
          </div>

          <div>
            <CustomAsyncMultiSelect
              promiseOptions={getBooksOptions}
              placeholder="Chọn sách"
              // styles={{
              //   stylesControl: {
              //     background: "#5570F114",
              //     border: "none",
              //     borderRadius: "0.5rem",
              //     color: "#5570F1",
              //     width: "200px",
              //     height: "36px",
              //   },
              //   styleSingle: {
              //     color: "#5570F1",
              //   },
              //   dropdownIndicatorStyles: {
              //     color: "#5570F1",
              //   },
              // }}
              onChange={(val) => setState({ bookIds: val })}
              value={state.bookIds}
            />
          </div>
        </div>
      </div>
      {state.voucherCode && (
        <div className="mt-7">
          <BarChart config={chartConfigUser} />
        </div>
      )}
      {state.voucherCode && (
        <div className="mt-7">
          <BarChart config={chartConfigInteraction} />
        </div>
      )}
      {state.voucherCode && (
        <div className="mt-7 w-full overflow-auto">
          <CustomTable
            columnConfig={columnConfig}
            dataTable={state.dataVoucherActiveInteraction}
            meta={state.metaVoucherActiveInteraction}
            hasStt
            componentEmpty={
              <div className="text-center py-3 bg-[#f4f4f4]">
                <p className="text-[#666] text-xl">Không có dữ liệu</p>
              </div>
            }
            maxHeight="500px"
            showPageSize={false}
            showPageChange={false}
          />
        </div>
      )}
    </div>
  );
};

export default memo(StatisticsVoucher);
